import React, {
  Children,
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useState,
} from "react";

import { createPortal } from "react-dom";

import { useSidebarContext } from "./Sidebar";

import type { JSX } from "react";

interface SubMenuPortalProps {
  isOpen: boolean;
  children: React.ReactNode;
  currentMenuRef: React.RefObject<HTMLDivElement | null>;
}

interface SubmenuChildProps {
  isInSubmenu?: boolean;
}

interface DropdownPosition {
  left: number;
  top: number;
}

export default function SubMenuPortal({
  isOpen,
  currentMenuRef,
  children,
}: SubMenuPortalProps): JSX.Element | null {
  const { isCollapsed } = useSidebarContext();
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);

  useLayoutEffect(() => {
    if (!isCollapsed || !isOpen || !currentMenuRef.current) {
      setDropdownPosition(null);
      return;
    }

    const { left, top, width } = currentMenuRef.current.getBoundingClientRect();

    setDropdownPosition({
      left: left + width,
      top,
    });
  }, [currentMenuRef, isCollapsed, isOpen]);

  if (!isCollapsed || !isOpen || !dropdownPosition) return null;

  const dropdownStyle = {
    left: `${dropdownPosition?.left ?? 0}px`,
    top: `${dropdownPosition?.top ?? 0}px`,
    filter: "drop-shadow(5px 4px 4px rgba(0, 0, 0, 0.07))",
    zIndex: 1300,
  };

  const childrenWithProps = Children.map(children, (child): React.ReactNode => {
    if (isValidElement<SubmenuChildProps>(child)) {
      return cloneElement(child, { isInSubmenu: true });
    }
    return child;
  });

  const collapsedSubmenuContent = (
    <div
      className="border-border bg-card fixed border-l drop-shadow-md"
      style={dropdownStyle}
    >
      {childrenWithProps}
    </div>
  );

  return createPortal(collapsedSubmenuContent, document.body);
}
